import { isWeightTotalComplete, sumWeights } from "@repo/shared";
import type { DynamicPortfolio } from "@repo/shared/types";
import { Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { AlertMessage } from "@/components/ui/alert-message";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Table,
  TableActionsCell,
  TableActionsHead,
  TableBody,
  TableCell,
  TableCellLink,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import * as pcApi from "@/lib/api/portfolio-collections";
import { useDeleteCheck } from "@/lib/hooks/use-delete-check";
import { useDeleteDynamicPortfolio, usePlatforms } from "@/lib/hooks/use-portfolio-collections";
import { useT } from "@/lib/i18n/use-t";

import { PlatformBadges, WeightTotalCell } from "../portfolio-weights";
import { DynamicPortfolioDialog } from "./dynamic-portfolio-dialog";
import { T } from "./dynamic-portfolios-table.i18n";

type DynamicPortfoliosTableProps = {
  portfolioCollectionId: string;
  dynamicPortfolios: DynamicPortfolio[];
};

export function DynamicPortfoliosTable({
  portfolioCollectionId,
  dynamicPortfolios,
}: DynamicPortfoliosTableProps) {
  const t = useT(T);
  const basePath = `/settings/portfolios/${portfolioCollectionId}/dynamic-portfolios`;

  const deleteDynamicPortfolio = useDeleteDynamicPortfolio();
  const { platforms } = usePlatforms(portfolioCollectionId);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPortfolio, setEditingPortfolio] = useState<DynamicPortfolio | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [portfolioToDelete, setPortfolioToDelete] = useState<string | null>(null);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    title: "",
    description: "",
    variant: "default" as "default" | "destructive",
  });

  const showAlert = (
    title: string,
    description: string,
    variant: "default" | "destructive" = "default",
  ) => {
    setAlertConfig({ title, description, variant });
    setAlertOpen(true);
  };

  // QL-168 — a dynamic portfolio the server would refuse to delete (required
  // while the collection is active) shows why on the Delete click, before the
  // confirmation.
  const { requestDelete, checkingId } = useDeleteCheck({
    check: (dynamicPortfolioId) =>
      pcApi.checkDeleteDynamicPortfolio(portfolioCollectionId, dynamicPortfolioId),
    onAllowed: (dynamicPortfolioId) => {
      setPortfolioToDelete(dynamicPortfolioId);
      setConfirmDeleteOpen(true);
    },
    onBlocked: (reason) => showAlert(t.error, reason || t.failedToDelete, "destructive"),
  });

  const handleDelete = async () => {
    if (!portfolioToDelete) return;

    deleteDynamicPortfolio.mutate(
      { collectionId: portfolioCollectionId, dynamicPortfolioId: portfolioToDelete },
      {
        onSuccess: () => {
          setPortfolioToDelete(null);
          toast.success(t.deleted);
        },
        onError: (err: Error) => {
          showAlert(t.error, err.message || t.failedToDelete, "destructive");
          setPortfolioToDelete(null);
        },
      },
    );
  };

  const isPending = deleteDynamicPortfolio.isPending;

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">{t.description}</p>
          <Button onClick={() => setDialogOpen(true)} disabled={isPending}>
            {t.addDynamicPortfolio}
          </Button>
        </div>

        {dynamicPortfolios.length === 0 ? (
          <EmptyState>{t.noDynamicPortfolios}</EmptyState>
        ) : (
          <Table variant="navigation">
            <TableHeader>
              <TableRow>
                <TableHead>{t.riskScore}</TableHead>
                <TableHead>{t.products}</TableHead>
                <TableHead>{t.platforms}</TableHead>
                <TableHead>{t.weight}</TableHead>
                <TableActionsHead>{t.actions}</TableActionsHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dynamicPortfolios.map((portfolio) => {
                const weights = portfolio.products.map((row) => row.weight);
                return (
                  <TableRow key={portfolio.id}>
                    <TableCellLink
                      to={`${basePath}/${portfolio.id}`}
                      label={`${t.riskScore} ${portfolio.riskScore}`}
                    >
                      <div className="text-lg font-bold">{portfolio.riskScore}</div>
                    </TableCellLink>
                    <TableCell className="text-muted-foreground">
                      {(portfolio.products.length === 1 ? t.productCount : t.productCountPlural)(
                        portfolio.products.length,
                      )}
                    </TableCell>
                    <TableCell>
                      <PlatformBadges platformIds={portfolio.platformIds} platforms={platforms} />
                    </TableCell>
                    <TableCell>
                      <WeightTotalCell
                        total={sumWeights(weights)}
                        isComplete={isWeightTotalComplete(weights)}
                      />
                    </TableCell>
                    <TableActionsCell>
                      <Button
                        size="icon"
                        tooltip={t.edit}
                        variant="ghost"
                        onClick={() => {
                          setEditingPortfolio(portfolio);
                          setDialogOpen(true);
                        }}
                        disabled={isPending}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        tooltip={t.delete}
                        variant="ghost"
                        onClick={() => requestDelete(portfolio.id)}
                        loading={checkingId === portfolio.id}
                        disabled={isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableActionsCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <DynamicPortfolioDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditingPortfolio(null);
        }}
        portfolioCollectionId={portfolioCollectionId}
        portfolio={editingPortfolio}
      />

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={t.deleteTitle}
        description={t.deleteDescription}
        confirmText={t.delete}
        cancelText={t.cancel}
        onConfirm={handleDelete}
        variant="destructive"
      />

      <AlertMessage
        open={alertOpen}
        onOpenChange={setAlertOpen}
        title={alertConfig.title}
        description={alertConfig.description}
        variant={alertConfig.variant}
      />
    </>
  );
}
